/**
 * useFunnelAbandon — survey-step abandon detection.
 *
 * Abandon trigger: a survey step is considered abandoned when either
 *   (a) the tab stays hidden ≥ 15 s while the user is parked on a non-review
 *       survey step, or
 *   (b) the route changes off `/survey` to a non-`/thankyou` destination.
 *
 * `useSurveyStepTracker` owns the `dedupedAbandons` Set — the trigger
 * arms from two sources concurrently and the contract is "at most one
 * effective abandon per step per page". The abandon side effect is the
 * dedup-flip + step-id clear; tests assert the dedup state.
 *
 * `currentSurveyStepId` lives on the tracker and is written by
 * `useSurvey` via `setCurrentStepId`; this composable reads it live.
 */

import { onBeforeUnmount, onMounted, watch } from "vue";
import type { Router } from "vue-router";
import { useRoute } from "vue-router";

import { useSurveyStepTracker } from "./useSurveyStepTracker.ts";

const ABANDON_HIDDEN_MS = 15_000;

export function useFunnelAbandon(router: Router) {
    const route = useRoute();
    const tracker = useSurveyStepTracker();

    let abandonHiddenTimer: ReturnType<typeof setTimeout> | null = null;

    function isOnSurveyRoute(): boolean {
        return route.name === "survey";
    }

    function clearAbandonTimer(): void {
        if (abandonHiddenTimer !== null) {
            clearTimeout(abandonHiddenTimer);
            abandonHiddenTimer = null;
        }
    }

    function onFunnelVisibilityChange(): void {
        if (typeof document === "undefined") return;
        const stepId = tracker.currentSurveyStepId.value;
        if (document.visibilityState === "hidden") {
            // Only arm the timer if we're parked on a non-review survey step.
            if (!isOnSurveyRoute() || !stepId || stepId === "review") return;
            clearAbandonTimer();
            abandonHiddenTimer = setTimeout(() => {
                // Re-check at fire time — the user may have come back and we
                // missed the visible event for some reason.
                if (
                    typeof document !== "undefined"
                    && document.visibilityState === "hidden"
                    && isOnSurveyRoute()
                    && tracker.currentSurveyStepId.value
                    && tracker.currentSurveyStepId.value !== "review"
                ) {
                    tracker.tryMarkAbandoned(tracker.currentSurveyStepId.value);
                }
                abandonHiddenTimer = null;
            }, ABANDON_HIDDEN_MS);
        } else {
            clearAbandonTimer();
        }
    }

    // Re-arm logic when the active step changes while the tab is hidden:
    // a stale timer may be pinned to the previous step. Clear it; if the
    // new step still qualifies and the tab is still hidden, the next
    // visibility tick (or the next route enter) re-evaluates.
    const stopWatchStep = watch(
        () => tracker.currentSurveyStepId.value,
        () => {
            // The hidden timer fires against the tracker's current id at
            // fire-time, so a stale armed timer is safe; but if the step
            // just transitioned to `review` or null, we should drop it now.
            const stepId = tracker.currentSurveyStepId.value;
            if (!stepId || stepId === "review") clearAbandonTimer();
        },
    );

    // Route-change abandon: leaving `/survey` for anything other than
    // `/thankyou` while parked on a non-review step counts as an abandon.
    const removeRouteGuard = router.beforeEach((to, from) => {
        const fromName = typeof from.name === "string" ? from.name : "";
        const toName = typeof to.name === "string" ? to.name : "";
        if (fromName !== "survey") return true;
        if (toName === "survey" || toName === "thankyou") return true;
        const stepId = tracker.currentSurveyStepId.value;
        if (stepId && stepId !== "review") {
            tracker.tryMarkAbandoned(stepId);
        }
        tracker.setCurrentStepId(null);
        clearAbandonTimer();
        return true;
    });

    onMounted(() => {
        if (typeof document !== "undefined") {
            document.addEventListener("visibilitychange", onFunnelVisibilityChange);
        }
    });

    onBeforeUnmount(() => {
        if (typeof document !== "undefined") {
            document.removeEventListener("visibilitychange", onFunnelVisibilityChange);
        }
        clearAbandonTimer();
        removeRouteGuard();
        stopWatchStep();
    });
}
